import { IconArchive, IconArrowRight } from "@tabler/icons-react";
import { Link } from "react-router-dom";

type SearchRowProps = {
  icon: React.ElementType;
  text?: string;
  subtitle?: string;
  isSelected: boolean;
  archived?: boolean;
  href?: string;
  onClick?: () => void;
};

export const SearchRow: React.FC<SearchRowProps> = ({
  icon: Icon,
  text = "",
  subtitle,
  isSelected,
  archived = false,
  href,
  onClick,
}) => {
  const className = `flex flex-row px-3 py-1 items-center justify-between rounded-md cursor-pointer transition-colors ${
    isSelected ? "surface-tertiary" : "hover:surface-secondary"
  }`;

  const content = (
    <>
      {/* Left: icon + single-line label · breadcrumb */}
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Icon
          size={14}
          className={`shrink-0 transition-colors ${
            archived
              ? "hint-orange-font"
              : isSelected
                ? "font-secondary"
                : "font-tertiary"
          }`}
        />
        <div className="flex items-center gap-1 min-w-0">
          {/* Title — truncates only as a last resort: the subtitle carries a
              4× shrink weight, so it always gives way first and the title
              only ellipsizes when it alone can't fit (375px phones). */}
          <span
            className={`whitespace-nowrap truncate min-w-0 transition-colors ${
              archived
                ? "body-3-medium hint-orange-font"
                : isSelected
                  ? "body-3-medium font-primary"
                  : "body-3-regular font-secondary"
            }`}
          >
            {text}
          </span>

          {archived && (
            <IconArchive size={12} className="hint-orange-font shrink-0" />
          )}

          {/* Separator + subtitle — subtitle truncates when space is tight */}
          {subtitle && (
            <>
              <span className="body-3-regular font-tertiary shrink-0 select-none">
                ·
              </span>
              <span className="body-3-regular font-tertiary truncate shrink-[4]">
                {subtitle}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Right: arrow, only visible when selected */}
      <IconArrowRight
        size={12}
        className={`shrink-0 ml-2 font-tertiary transition-opacity ${
          isSelected ? "opacity-100" : "opacity-0"
        }`}
      />
    </>
  );

  const testAttrs = {
    "data-testid": "psearch-row",
    "data-psearch-url": href ?? "",
    "data-psearch-label": text,
    "data-psearch-archived": archived ? "true" : "false",
  };

  if (href) {
    return (
      <Link
        to={href}
        className={className}
        {...testAttrs}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          e.preventDefault();
          onClick?.();
        }}
      >
        {content}
      </Link>
    );
  }

  return (
    <div onClick={onClick} className={className} {...testAttrs}>
      {content}
    </div>
  );
};
